import type { Brand, BrandColorKey, BrandTokens, CurrencyFormat, Palette, ResolvedBrand, ResolvedTheme } from '../types';
import { isColor, withAlpha } from '../util/color';
import { deepMerge, resolveAsset, resolveFont } from './defaults';

export const BRAND_COLOR_KEYS: BrandColorKey[] = [
  'hudBackground',
  'text',
  'textMuted',
  'accent',
  'accentText',
  'buttonPrimary',
  'buttonPrimaryText',
  'buttonSecondary',
  'buttonSecondaryText',
  'overlayBackdrop',
  'background',
  'backgroundFreeSpins',
  'reelBackdrop',
  'reelSeparator',
  'reelFrame',
  'symbolDim',
  'winNormal',
  'winBig',
  'winMega',
  'winEpic',
  'anticipationGlow',
  'wildGlow',
  'scatterGlow',
  'loss',
];

export const BRAND_TOKEN_KEYS: (keyof BrandTokens)[] = ['page', 'pageInk', 'machine', 'machineDeep', 'panel', 'panelSoft', 'primary', 'primaryInk', 'accent', 'line', 'loss'];

/**
 * Expand Lucky Fruit design tokens into palette colours.
 *   page -> background / backgroundFreeSpins, pageInk -> text, machine -> reelBackdrop,
 *   machineDeep -> overlayBackdrop, panel -> hudBackground, panelSoft -> buttonSecondary,
 *   primary -> buttonPrimary + accent + winNormal + anticipationGlow, primaryInk -> buttonPrimaryText + accentText,
 *   accent -> winBig + scatterGlow, line -> reelSeparator, loss -> loss.
 */
export function tokensToColors(t: BrandTokens): Partial<Pick<Palette, BrandColorKey>> {
  const c: Partial<Pick<Palette, BrandColorKey>> = {};
  if (t.page) {
    c.background = t.page;
    c.backgroundFreeSpins = t.page;
  }
  if (t.pageInk) c.text = t.pageInk;
  if (t.machine) c.reelBackdrop = t.machine;
  if (t.machineDeep) c.overlayBackdrop = withAlpha(t.machineDeep, 0.8);
  if (t.panel) c.hudBackground = withAlpha(t.panel, 0.92);
  if (t.panelSoft) {
    c.buttonSecondary = t.panelSoft;
    if (t.pageInk) c.buttonSecondaryText = t.pageInk;
  }
  if (t.primary) {
    c.buttonPrimary = t.primary;
    c.accent = t.primary;
    c.winNormal = t.primary;
    c.anticipationGlow = t.primary;
  }
  if (t.primaryInk) {
    c.buttonPrimaryText = t.primaryInk;
    c.accentText = t.primaryInk;
  }
  if (t.accent) {
    c.winBig = t.accent;
    c.scatterGlow = t.accent;
  }
  if (t.line) c.reelSeparator = t.line;
  if (t.loss) c.loss = t.loss;
  return c;
}

/** Neutral brand: no changes to the theme chrome. */
export const DEFAULT_BRAND: Omit<ResolvedBrand, 'id' | 'name' | 'baseUrl'> = {
  defaultTheme: null,
  tokens: {},
  ui: { colors: {}, hudBorder: 'transparent', modalBackground: null, spinGradient: null, focusRing: null },
  buttons: { spinOrbit: false, spinArt: true, radius: 12, spinShape: 'circle', spinSize: 84, style: 'flat', uppercase: false, shadow: true },
  hud: { style: 'bar', position: 'bottom', showPaytable: true, showSettings: true, showSound: true, showWin: true, showBalance: true, showMaxBet: true },
  logo: null,
  watermark: null,
  icons: {},
  fonts: {},
  strings: {},
  currency: {},
  css: null,
  loadingBackground: null,
  title: null,
};

/** Merge a brand.json (partial) over the defaults and resolve asset paths against the brand folder. */
export function mergeBrand(brand: Brand, baseUrl: string): ResolvedBrand {
  if (!baseUrl.endsWith('/')) baseUrl += '/';
  const merged = deepMerge(DEFAULT_BRAND as unknown as ResolvedBrand, brand) as ResolvedBrand;
  merged.id = brand.id;
  merged.name = brand.name ?? brand.id;
  merged.baseUrl = baseUrl;
  const r = (p?: string | null) => (p ? resolveAsset(p, baseUrl) : p ?? null);
  if (merged.logo?.src) merged.logo = { ...merged.logo, src: resolveAsset(merged.logo.src, baseUrl) };
  else merged.logo = null;
  if (merged.watermark?.src) merged.watermark = { ...merged.watermark, margin: merged.watermark.margin ?? 12, src: resolveAsset(merged.watermark.src, baseUrl) };
  else merged.watermark = null;
  const icons: ResolvedBrand['icons'] = {};
  for (const k of ['info', 'settings', 'sound', 'mute'] as const) if (merged.icons?.[k]) icons[k] = resolveAsset(merged.icons[k]!, baseUrl);
  merged.icons = icons;
  merged.css = r(merged.css);
  merged.fonts = { display: resolveFont(merged.fonts?.display, baseUrl), ui: resolveFont(merged.fonts?.ui, baseUrl), mono: resolveFont(merged.fonts?.mono, baseUrl) };
  merged.tokens = { ...(merged.tokens ?? {}) };
  merged.ui = { ...DEFAULT_BRAND.ui, ...merged.ui, colors: { ...(merged.ui?.colors ?? {}) } };
  merged.strings = { ...(merged.strings ?? {}) };
  merged.currency = { ...(merged.currency ?? {}) };
  return merged;
}

/**
 * Apply a brand to a resolved theme: chrome colours, fonts and strings.
 * Returns a NEW theme object; the game scene (symbols, reels, backgrounds) is untouched.
 */
export function applyBrand(theme: ResolvedTheme, brand: ResolvedBrand | null): ResolvedTheme {
  if (!brand) return theme;
  const palette: Palette = { ...theme.palette };
  const colors = { ...tokensToColors(brand.tokens), ...brand.ui.colors };
  for (const k of BRAND_COLOR_KEYS) {
    const v = colors[k];
    if (typeof v === 'string') palette[k] = v;
  }
  const strings: ResolvedTheme['strings'] = { ...theme.strings };
  for (const [k, v] of Object.entries(brand.strings)) {
    const prev = strings[k];
    if (typeof v === 'object' && v && typeof prev === 'object' && prev) strings[k] = { ...prev, ...v };
    else strings[k] = v;
  }
  const title = typeof brand.title === 'string' ? brand.title.trim() : '';
  const customTitle = title && title.length <= 40 && title !== theme.name ? title : undefined;
  return {
    ...theme,
    customTitle,
    palette,
    fonts: {
      display: brand.fonts.display ? { ...theme.fonts.display, ...brand.fonts.display } : theme.fonts.display,
      ui: brand.fonts.ui ? { ...theme.fonts.ui, ...brand.fonts.ui } : theme.fonts.ui,
      mono: brand.fonts.mono ? { ...(theme.fonts.mono ?? {}), ...brand.fonts.mono } : theme.fonts.mono,
    },
    strings,
  };
}

/** Currency = defaults + brand + explicit options (options win). */
export function brandCurrency(base: CurrencyFormat, brand: ResolvedBrand | null, override?: Partial<CurrencyFormat>): CurrencyFormat {
  return { ...base, ...(brand?.currency ?? {}), ...(override ?? {}) };
}

/** Extra CSS custom properties for the brand chrome (the palette itself is emitted by paletteToCssVars). */
export function brandCssVars(brand: ResolvedBrand | null, theme: ResolvedTheme): Record<string, string> {
  const b = brand ?? ({ ...DEFAULT_BRAND, id: 'default', name: 'default', baseUrl: '' } as ResolvedBrand);
  const spin = b.buttons;
  const radius = spin.spinShape === 'circle' ? '50%' : spin.spinShape === 'pill' ? '999px' : `${Math.round(spin.spinSize * 0.22)}px`;
  const width = spin.spinShape === 'pill' ? `${Math.round(spin.spinSize * 1.6)}px` : `${spin.spinSize}px`;
  const primary = theme.palette.buttonPrimary;
  const spinBg = spin.style === 'gradient' && b.ui.spinGradient ? `linear-gradient(135deg, ${b.ui.spinGradient[0]}, ${b.ui.spinGradient[1]})` : primary;
  const tokenVars: Record<string, string> = {};
  for (const k of BRAND_TOKEN_KEYS) {
    const v = b.tokens[k];
    if (v) tokenVars[`--slot-token-${k.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}`] = v;
  }
  return {
    ...tokenVars,
    '--slot-btn-radius': `${spin.radius}px`,
    '--slot-spin-radius': radius,
    '--slot-spin-size': `${spin.spinSize}px`,
    '--slot-spin-width': width,
    '--slot-spin-bg': spinBg,
    '--slot-text-transform': spin.uppercase ? 'uppercase' : 'none',
    '--slot-spin-shadow': spin.shadow ? '0 4px 0 rgba(0,0,0,.35), 0 0 0 4px var(--slot-hud-background)' : 'none',
    '--slot-hud-border': b.ui.hudBorder || 'transparent',
    '--slot-modal-background': b.ui.modalBackground ?? theme.palette.background,
    '--slot-focus-ring': b.ui.focusRing ?? theme.palette.accent,
    '--slot-loading-background': b.loadingBackground ?? theme.palette.background,
  };
}

/** Problems in a brand manifest (empty = valid). */
export function validateBrand(brand: ResolvedBrand): string[] {
  const p: string[] = [];
  for (const [k, v] of Object.entries(brand.tokens)) {
    if (!BRAND_TOKEN_KEYS.includes(k as keyof BrandTokens)) p.push(`tokens.${k} is not a known token (allowed: ${BRAND_TOKEN_KEYS.join(', ')})`);
    else if (typeof v !== 'string' || !isColor(v)) p.push(`tokens.${k} is not a colour: ${String(v)}`);
  }
  for (const [k, v] of Object.entries(brand.ui.colors)) {
    if (!BRAND_COLOR_KEYS.includes(k as BrandColorKey)) p.push(`ui.colors.${k} is not a brandable colour (allowed: ${BRAND_COLOR_KEYS.join(', ')})`);
    else if (typeof v !== 'string' || !isColor(v)) p.push(`ui.colors.${k} is not a colour: ${String(v)}`);
  }
  if (brand.ui.hudBorder !== 'transparent' && !isColor(brand.ui.hudBorder)) p.push(`ui.hudBorder is not a colour: ${brand.ui.hudBorder}`);
  for (const k of ['modalBackground', 'focusRing'] as const) {
    const v = brand.ui[k];
    if (v != null && !isColor(v)) p.push(`ui.${k} is not a colour: ${v}`);
  }
  if (brand.ui.spinGradient && (brand.ui.spinGradient.length !== 2 || !brand.ui.spinGradient.every(isColor))) p.push('ui.spinGradient must be two colours');
  if (brand.loadingBackground != null && !isColor(brand.loadingBackground)) p.push(`loadingBackground is not a colour: ${brand.loadingBackground}`);
  const bt = brand.buttons;
  if (!['circle', 'rounded', 'pill'].includes(bt.spinShape)) p.push(`buttons.spinShape "${bt.spinShape}" must be circle | rounded | pill`);
  if (bt.spinArt != null && typeof bt.spinArt !== 'boolean') p.push('buttons.spinArt must be a boolean');
  if (brand.title != null && (typeof brand.title !== 'string' || !brand.title.trim() || brand.title.trim().length > 40)) p.push('title must be 1–40 characters');
  if (!['flat', 'gradient', 'outline'].includes(bt.style)) p.push(`buttons.style "${bt.style}" must be flat | gradient | outline`);
  if (bt.style === 'gradient' && !brand.ui.spinGradient) p.push('buttons.style is "gradient" but ui.spinGradient is not set');
  if (!(bt.spinSize >= 44 && bt.spinSize <= 200)) p.push('buttons.spinSize must be 44..200');
  if (!(bt.radius >= 0)) p.push('buttons.radius must be >= 0');
  if (!['bar', 'floating', 'minimal'].includes(brand.hud.style)) p.push(`hud.style "${brand.hud.style}" must be bar | floating | minimal`);
  if (!['bottom', 'top'].includes(brand.hud.position)) p.push(`hud.position "${brand.hud.position}" must be bottom | top`);
  if (brand.logo) {
    if (!['hud-left', 'hud-center', 'hud-right', 'top-left', 'top-right'].includes(brand.logo.position)) p.push(`logo.position "${brand.logo.position}" is invalid`);
    if (!(brand.logo.height > 0)) p.push('logo.height must be positive');
  }
  if (brand.watermark) {
    if (!['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(brand.watermark.position)) p.push(`watermark.position "${brand.watermark.position}" is invalid`);
    if (!(brand.watermark.width > 0)) p.push('watermark.width must be positive');
    if (!(brand.watermark.alpha >= 0 && brand.watermark.alpha <= 1)) p.push('watermark.alpha must be 0..1');
  }
  const c = brand.currency;
  if (c.position != null && !['prefix', 'suffix'].includes(c.position)) p.push('currency.position must be prefix | suffix');
  if (c.decimals != null && !(Number.isInteger(c.decimals) && c.decimals >= 0 && c.decimals <= 4)) p.push('currency.decimals must be 0..4');
  return p;
}
